/**
 * <archive-chapters> — the home page's chaptered story block.
 *
 * The chapters are read by scrolling down. Every chapter is in the page, one
 * after another; the media is pinned (CSS position: sticky) and turns to
 * whichever chapter the reader is in:
 *   - desktop (≥990px): the media sits beside the chapters and wipes to a
 *     chapter once it reaches the middle of the screen;
 *   - phone and tablet: the media pins at the top and each chapter slides up
 *     over it like a card; the media turns while it is covered, so the next
 *     chapter's picture is waiting in the gap between cards.
 * The numbered buttons, the arrows and a chapter's "next" button scroll to a
 * chapter rather than switching it. A red line under the list shows how far
 * through the chapters the reader is (--chapters-p).
 *
 * Nothing moves on its own: one chapter holds the real buy form, and the
 * reader's own scroll is the only thing that turns the page. See design.md.
 *
 * Without the script every chapter is simply stacked under the media.
 */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const sideBySide = window.matchMedia('(min-width: 990px)');

/* aria-hidden is only meaningful as the string "true" — an empty attribute
   value does not hide anything. */
const setHidden = (element, hidden) => {
  if (hidden) {
    element.setAttribute('aria-hidden', 'true');
  } else {
    element.removeAttribute('aria-hidden');
  }
};

class ArchiveChapters extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('[data-chapter-panel]'));
    this.tabs = Array.from(this.querySelectorAll('[data-chapter-tab]'));
    this.mediaPanels = Array.from(this.querySelectorAll('[data-chapter-media]'));
    this.captions = Array.from(this.querySelectorAll('[data-chapter-caption]'));
    this.media = this.querySelector('[data-chapters-media]');

    if (this.panels.length === 0) return;

    this.index = -1;
    this.classList.add('is-interactive');

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.goTo(i));
    });

    this.prev = this.querySelector('[data-chapters-prev]');
    this.next = this.querySelector('[data-chapters-next]');
    if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.index - 1));
    if (this.next) this.next.addEventListener('click', () => this.goTo(this.index + 1));

    // A chapter's own CTA can walk the reader on to the next one.
    this.querySelectorAll('[data-chapters-next-cta]').forEach((button) => {
      const panel = button.closest('[data-chapter-panel]');
      button.addEventListener('click', () => this.goTo(this.panels.indexOf(panel) + 1));
    });

    this.ticking = false;
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };
    this.onResize = () => {
      this.measure();
      this.onScroll();
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    sideBySide.addEventListener('change', this.onResize);
    // Images and the buy form can change the media's height after load.
    if (this.media && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(this.media);
    }

    this.measure();
    this.update();
    // The header's logo and fonts can settle after this runs.
    window.addEventListener('load', this.onResize, { once: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    sideBySide.removeEventListener('change', this.onResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  // The header's real height (Dawn's --header-height reads 0 where the
  // header is transparent and overlays the page, as on the home page), and,
  // for a phone, the media's own height: the gap between chapter cards, so
  // the whole picture shows between them.
  measure() {
    const header = Math.max(
      0,
      ...Array.from(document.querySelectorAll('.section-header sticky-header, .section-header .header-wrapper, .section-header .header'))
        .map((el) => el.getBoundingClientRect().height)
    );
    this.style.setProperty('--chapters-header', `${Math.round(header)}px`);
    if (!this.media) return;
    this.style.setProperty('--chapters-media-h', `${Math.round(this.media.offsetHeight)}px`);
  }

  // Which chapter the reader is in.
  current() {
    const height = window.innerHeight;
    let index = 0;
    this.panels.forEach((panel, i) => {
      if (i === 0) return;
      if (sideBySide.matches) {
        // Beside the media: a chapter is current once its top passes mid-screen.
        if (panel.getBoundingClientRect().top <= height * 0.5) index = i;
      } else if (this.panels[i - 1].getBoundingClientRect().bottom <= height * 0.85) {
        // Stacked: turn while the previous card still covers the media.
        index = i;
      }
    });
    return index;
  }

  // Where the header ends on screen, if it is on screen at all.
  headerBottom() {
    const header = document.querySelector('.section-header sticky-header, .section-header .header-wrapper');
    if (!header) return 0;
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  }

  update() {
    // The header slides in and out on its own animation, so look again once
    // it has finished.
    const top = this.headerBottom();
    if (top !== this.top) {
      this.top = top;
      this.style.setProperty('--chapters-top', `${top}px`);
    }
    clearTimeout(this.headerTimer);
    this.headerTimer = setTimeout(() => {
      const settled = this.headerBottom();
      if (settled === this.top) return;
      this.top = settled;
      this.style.setProperty('--chapters-top', `${settled}px`);
    }, 350);

    const index = this.current();
    if (index !== this.index) this.show(index);

    // How far through the chapters, for the red line under the list.
    const first = this.panels[0].getBoundingClientRect();
    const last = this.panels[this.panels.length - 1].getBoundingClientRect();
    const span = last.bottom - first.top - window.innerHeight;
    const progress = span > 0 ? Math.min(1, Math.max(0, -first.top / span)) : 0;
    this.style.setProperty('--chapters-p', progress.toFixed(4));
  }

  show(index) {
    const previous = this.index;
    this.index = index;
    // Going down, the next picture rises from below; going back, from above.
    // The direction has to land before the picture turns, so the wipe starts
    // from the right edge.
    this.classList.toggle('is-going-up', previous > index);
    void this.offsetWidth;

    this.panels.forEach((panel, i) => panel.classList.toggle('is-active', i === index));

    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('is-active', active);
      if (active) tab.setAttribute('aria-current', 'step');
      else tab.removeAttribute('aria-current');
    });

    this.mediaPanels.forEach((panel, i) => {
      const active = i === index;
      // The outgoing picture stays underneath until the new one has covered it.
      panel.classList.toggle('is-leaving', i === previous && !active);
      panel.classList.toggle('is-active', active);
      setHidden(panel, !active);
    });
    clearTimeout(this.leaveTimer);
    this.leaveTimer = setTimeout(() => {
      this.mediaPanels.forEach((panel) => panel.classList.remove('is-leaving'));
    }, 1000);

    this.captions.forEach((caption, i) => {
      const active = i === index;
      caption.classList.toggle('is-active', active);
      setHidden(caption, !active);
    });

    // There is nothing before the first chapter or after the last.
    if (this.prev) this.prev.disabled = index === 0;
    if (this.next) this.next.disabled = index === this.panels.length - 1;
  }

  // Scroll so a chapter is the current one: beside the media, its top under
  // the header; stacked, its card just below the pinned media so the
  // chapter's picture shows first.
  goTo(index) {
    const target = this.panels[Math.max(0, Math.min(index, this.panels.length - 1))];
    if (!target) return;
    const header = parseFloat(getComputedStyle(this).getPropertyValue('--chapters-top')) || 0;
    const offset = sideBySide.matches ? header : (this.media ? this.media.offsetHeight : 0) + header;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top + 1), behavior: reduceMotion() ? 'auto' : 'smooth' });
  }
}

customElements.define('archive-chapters', ArchiveChapters);

/* ------------------------------------------------------------------ */

/**
 * The product chapter's media column shows several product images. They cycle
 * on a slow interval so the column has some life, pausing off-screen, on hover
 * and under reduced motion.
 */
const cycleMedia = (root) => {
  root.querySelectorAll('[data-chapter-media]').forEach((panel) => {
    const images = Array.from(panel.querySelectorAll('.archive-chapters__image'));
    if (images.length < 2) return;

    let i = images.findIndex((image) => image.classList.contains('is-active'));
    if (i < 0) {
      i = 0;
      images[0].classList.add('is-active');
    }

    if (reduceMotion()) return;

    let timer = null;
    const step = () => {
      images[i].classList.remove('is-active');
      i = (i + 1) % images.length;
      images[i].classList.add('is-active');
    };

    const start = () => {
      if (timer) return;
      timer = setInterval(step, 4000);
    };
    const stop = () => {
      clearInterval(timer);
      timer = null;
    };

    panel.addEventListener('pointerenter', stop);
    panel.addEventListener('pointerleave', start);
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

    new IntersectionObserver(
      (entries) => entries.forEach((entry) => (entry.isIntersecting ? start() : stop())),
      { threshold: 0.2 }
    ).observe(panel);
  });
};

document.querySelectorAll('archive-chapters').forEach(cycleMedia);
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.('archive-chapters').forEach(cycleMedia);
});
